import { Formik, FormikHelpers } from 'formik';

import { AvailableOrganizer } from 'modules/organizers/domain/interfaces/available-organizer';
import { getSportTypeLabel } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { isEmptyAddress } from 'modules/core/helpers/is-empty-address';
import { Pages } from 'modules/navigation/domain/enums/pages';
import organizerFormValidator from 'modules/organizer/domain/validators/organizer.validator';
import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Background, backgrounds } from 'shared/constants/backgrounds';
import { usePreventNavigation } from 'shared/hooks';
import { useImageUpscaleFieldOption } from 'shared/hooks/use-image-upscale-access';
import type { MessageDescriptor } from 'shared/i18n';
import { getAvailableTournamentTypes } from 'shared/utils';
import { UDFormLogoField } from 'ud-form/components/logo-field';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormSelect, { UDFormAddressSelect, UDFormCreatableSelect } from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';
import {
  mapAddressToDadataArea,
  mapDadataAreaToAddress,
} from 'ud-ui/components/dadata-address-autocomplete/infra/data-mappers/dadata-area.data-mapper';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';
import { DEFAULT_ORGANIZER_TIMEZONE, ORGANIZER_TIMEZONE_OPTIONS } from 'modules/organizer/domain/constants/timezones';

type SportTypeOption = {
  label: string;
  value: string;
};

type DadataAreaType = any; // Заменить на правильный тип, когда будет доступен

export interface OrganizerFormValues {
  name: string;
  sportType: SportTypeOption;
  email?: string;
  phone?: string;
  address?: DadataAreaType;
  description?: string;
  logo: {
    img: string | File;
    background: Background;
  }[];
  timezone: SportTypeOption;
}

type Translate = (descriptor: MessageDescriptor) => string;

const timezoneToSelectOption = (timezone: string, translate: Translate): SportTypeOption => {
  const known = ORGANIZER_TIMEZONE_OPTIONS.find((option) => option.value === timezone);
  return {
    value: timezone,
    label: known ? translate(known.label) : timezone,
  };
};

const timezoneOptions = (translate: Translate): SportTypeOption[] =>
  ORGANIZER_TIMEZONE_OPTIONS.map((option) => ({
    value: option.value,
    label: translate(option.label),
  }));

const sportTypeToSelectOption = (sportType: string, translate: Translate): SportTypeOption => {
  const sportTypeKey = sportType.toLowerCase();
  const label = getSportTypeLabel(sportTypeKey);
  return {
    label: label ? translate(label) : sportTypeKey,
    value: sportTypeKey,
  };
};

const sportTypesToSelectOptions = (availableTournaments: AvailableOrganizer[], translate: Translate): SportTypeOption[] =>
  getAvailableTournamentTypes(availableTournaments).map((sportType) => sportTypeToSelectOption(sportType, translate));

type OrganizerFormProps = {
  isLoading?: boolean;
  initialValues?: OrganizerUpsertPayload & { backgroundColor?: Background };
  /** Возвращает промис запроса: по нему «Сохранить» заперта до ответа (IMGM-1561). */
  onSubmit: (values: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) => Promise<unknown>;
};

export const OrganizerForm = ({ isLoading, initialValues, onSubmit }: OrganizerFormProps) => {
  const { t } = useLingui();
  const logoUpscale = useImageUpscaleFieldOption('graphic');

  // TODO: add filter by available sport type
  const sportTypeOptions = sportTypesToSelectOptions([], t);

  const [isFormDirty, setIsFormDirty] = useState(false);
  // `id` есть только у правки: страница создания передаёт лишь телефон пользователя.
  const isEditing = Boolean(initialValues?.id);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  // Мемо, а не литерал на каждый рендер: у формы `enableReinitialize`, и новый объект после `setIsFormDirty` в сабмите
  // сбрасывал её — снимал замок «Сохранить» (`isSubmitting`) и откатывал поля к исходным, пока запрос ещё шёл.
  const initialFormValues = useMemo<OrganizerFormValues>(
    () =>
      initialValues
        ? {
            ...initialValues,
            logo: [
              {
                img: initialValues.logoUrl ?? '',
                background: initialValues.backgroundColor ?? backgrounds[0],
              },
            ],
            address: isEmptyAddress(initialValues.address) ? undefined : mapAddressToDadataArea(initialValues.address!),
            sportType: initialValues.sportType
              ? sportTypeToSelectOption(initialValues.sportType, t)
              : sportTypeToSelectOption(SportType.football, t),
            timezone: timezoneToSelectOption(initialValues.timezone ?? DEFAULT_ORGANIZER_TIMEZONE, t),
          }
        : {
            name: '',
            sportType: sportTypeToSelectOption(SportType.football, t),
            phone: '',
            logo: [{ img: '', background: backgrounds[0] }],
            timezone: timezoneToSelectOption(DEFAULT_ORGANIZER_TIMEZONE, t),
          },
    [initialValues, t],
  );

  const handleSubmit = useCallback(
    (values: OrganizerFormValues, formikHelpers: FormikHelpers<OrganizerFormValues>) => {
      const { address: changedAddress } = values;
      const address = changedAddress ? mapDadataAreaToAddress(changedAddress) : changedAddress;

      const data = {
        ...values,
        address,
        sportType: values.sportType.value,
        timezone: values.timezone.value,
      };

      return onSubmit(data as OrganizerUpsertPayload, formikHelpers);
    },
    [onSubmit],
  );

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.CHAMPIONSHIPS.INDEX,
  });

  return (
    <Formik<OrganizerFormValues>
      enableReinitialize
      initialValues={initialFormValues}
      validationSchema={organizerFormValidator()}
      onSubmit={(values, formikHelpers) => {
        // Создание: после него уходят со страницы, поэтому база — текущие значения сразу (иначе «Покинуть?»);
        // `isSubmitting: true` переживает сброс — кнопку держит промис запроса. Правка («Профиль») остаётся на
        // экране: форму делает чистой переинициализация из сохранённых данных, а отказ оставляет правки и даёт
        // повторить.
        if (!isEditing) {
          formikHelpers.resetForm({ values, isSubmitting: true });
          setIsFormDirty(false);
        }
        return handleSubmit(values, formikHelpers);
      }}
      validateOnMount
    >
      {(formProps) => {
        setIsFormDirty(formProps.dirty);
        return (
          <form onSubmit={formProps.handleSubmit}>
            <div className="flex gap-6">
              <div className="w-1/2">
                <UDFormInput
                  disabled={isLoading}
                  name="name"
                  label={t`Название организации*`}
                  placeholder={t`Введите название`}
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormSelect
                  isDisabled={isLoading}
                  name="sportType"
                  label={t`Тип спорта`}
                  placeholder={t`Выберите тип спорта`}
                  isMulti={false}
                  options={sportTypeOptions}
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormInput
                  disabled={isLoading}
                  name="email"
                  label="Email"
                  placeholder="Email"
                  containerProps={{ className: 'mb-5' }}
                />

                <UDInputPhone
                  name="phone"
                  label={t`Телефон`}
                  placeholder={t`Введите номер телефона`}
                  wrapperProps={{ className: 'mb-6' }}
                  value={formProps.values.phone}
                />

                <UDFormAddressSelect
                  name="address"
                  label={t`Город`}
                  placeholder={t`Введите адрес`}
                  containerProps={{ className: 'mb-5' }}
                  onlyCity={true}
                />

                <UDFormCreatableSelect
                  name="timezone"
                  label={t`Часовой пояс`}
                  placeholder={t`Выберите часовой пояс`}
                  options={timezoneOptions(t)}
                  formatCreateLabel={(input) => input}
                  containerProps={{ className: 'mb-5' }}
                />
                <p className="text-inkMuted -mt-3 mb-5 text-sm">{t`Используется для времени анонсов и дат на картинках`}</p>

                <UDFormTextarea
                  disabled={isLoading}
                  name="description"
                  label={t`Описание`}
                  placeholder={t`Введите текст`}
                  containerProps={{ className: 'mb-5' }}
                />
              </div>
              <div className="w-1/2">
                <UDFormLogoField name="logo" label={t`Обложка и изображение`} crop upscale={logoUpscale} className="p-6" />
              </div>
            </div>
            <div className="mt-6 mb-2 flex justify-end">
              <UDButton
                variant="primary"
                type="submit"
                className="mb-3"
                disabled={isLoading || !formProps.dirty || !formProps.isValid}
              >
                {t`Сохранить`}
              </UDButton>
            </div>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};
